/**
 * Active Cycles Page - Hero Section
 * Primary workflow page showing all active disclosure cycles
 * 
 * Connected to backend API: GET /api/v1/disclosure-cycles/list
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  ChevronRight,
  Plus,
  Filter,
  Search,
  ArrowUpRight,
  Zap,
  Upload,
  GitMerge,
  Shield,
  FileBarChart,
  RefreshCw,
  User
} from 'lucide-react';
import { API } from '../../App';
import { apiFetch } from '../../services/api_service';

// Stage configuration mapping backend stages to display
const STAGE_CONFIG = {
  upload: { label: 'Upload', icon: Upload, colorClass: 'emerald', index: 0 },
  mapping: { label: 'Mapping', icon: GitMerge, colorClass: 'blue', index: 1 },
  evidence_review: { label: 'Evidence', icon: FileText, colorClass: 'purple', index: 2 },
  audit_trail: { label: 'Audit', icon: Shield, colorClass: 'amber', index: 3 },
  report: { label: 'Report', icon: FileBarChart, colorClass: 'cyan', index: 4 },
};

// Pre-defined color classes (Tailwind needs static class names)
const COLOR_CLASSES = {
  emerald: {
    bg: 'bg-emerald-500/30',
    ring: 'ring-emerald-500/50',
    bgLight: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    bar: 'bg-emerald-500',
  },
  blue: {
    bg: 'bg-blue-500/30',
    ring: 'ring-blue-500/50',
    bgLight: 'bg-blue-500/20',
    text: 'text-blue-400',
    bar: 'bg-blue-500',
  },
  purple: {
    bg: 'bg-purple-500/30',
    ring: 'ring-purple-500/50',
    bgLight: 'bg-purple-500/20',
    text: 'text-purple-400',
    bar: 'bg-purple-500',
  },
  amber: {
    bg: 'bg-amber-500/30',
    ring: 'ring-amber-500/50',
    bgLight: 'bg-amber-500/20',
    text: 'text-amber-400',
    bar: 'bg-amber-500',
  },
  cyan: {
    bg: 'bg-cyan-500/30',
    ring: 'ring-cyan-500/50',
    bgLight: 'bg-cyan-500/20',
    text: 'text-cyan-400',
    bar: 'bg-cyan-500',
  },
};

const STATUS_COLORS = {
  draft: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', label: 'Draft' },
  in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Progress' },
  in_review: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'In Review' },
  approved: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Approved' },
  published: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Published' },
};

// Compact flywheel progress indicator
const FlywheelMini = ({ currentStage }) => {
  const stages = ['upload', 'mapping', 'evidence_review', 'audit_trail', 'report'];
  const currentIndex = stages.indexOf(currentStage);
  
  return (
    <div className="flex items-center gap-1">
      {stages.map((stage, idx) => {
        const config = STAGE_CONFIG[stage];
        const colors = COLOR_CLASSES[config.colorClass];
        const Icon = config.icon;
        const isActive = idx === currentIndex;
        const isComplete = idx < currentIndex;
        
        return (
          <React.Fragment key={stage}>
            <div 
              className={`p-1.5 rounded-md transition-all ${
                isActive ? `${colors.bg} ring-1 ${colors.ring}` :
                isComplete ? 'bg-emerald-500/20' : 'bg-[#1A1A1A]'
              }`}
              title={config.label}
            >
              <Icon 
                size={12} 
                className={
                  isActive ? colors.text :
                  isComplete ? 'text-emerald-400' : 'text-[#4A4A4A]'
                } 
              />
            </div>
            {idx < stages.length - 1 && (
              <div className={`w-3 h-0.5 rounded-full ${
                idx < currentIndex ? 'bg-emerald-500/50' : 'bg-[#2A2A2A]'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CycleCard = ({ cycle }) => {
  const stage = cycle.current_stage || 'upload';
  const stageConfig = STAGE_CONFIG[stage] || STAGE_CONFIG.upload;
  const colors = COLOR_CLASSES[stageConfig.colorClass];
  const StageIcon = stageConfig.icon;
  
  // Calculate days until deadline
  const deadline = cycle.filing_deadline ? new Date(cycle.filing_deadline) : null;
  const today = new Date();
  const daysLeft = deadline ? Math.ceil((deadline - today) / (1000 * 60 * 60 * 24)) : null;
  
  // Determine urgency status
  let urgencyStatus = 'normal';
  if (daysLeft !== null) {
    if (daysLeft < 0) urgencyStatus = 'overdue';
    else if (daysLeft <= 14) urgencyStatus = 'urgent';
    else if (daysLeft <= 30) urgencyStatus = 'warning';
  }
  
  const statusConfig = STATUS_COLORS[cycle.status] || STATUS_COLORS.draft;
  const progress = cycle.overall_progress || 0;
  const coverage = cycle.requirements_coverage || { covered: 0, total: 0 };

  return (
    <Link 
      to={`/cycles/${cycle.id}`}
      className="block bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-5 hover:border-emerald-500/40 hover:bg-[#111111] transition-all group"
      data-testid={`cycle-card-${cycle.id}`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          {/* Tags */}
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#1A1A1A] text-[#808080] rounded">
              {cycle.framework_code}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${statusConfig.bg} ${statusConfig.text}`}>
              {statusConfig.label}
            </span>
          </div>
          {/* Title */}
          <h3 className="text-base font-semibold text-white group-hover:text-emerald-400 transition-colors truncate">
            {cycle.cycle_name}
          </h3>
        </div>
        <ArrowUpRight size={16} className="text-[#4A4A4A] group-hover:text-emerald-400 transition-colors shrink-0 mt-1" />
      </div>

      {/* Progress Section */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-[#6B6B6B]">Progress</span>
          <span className="text-white font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${colors.bar}`}
            style={{ width: `${Math.max(progress, 2)}%` }}
          />
        </div>
      </div>

      {/* Stage Indicator */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#1A1A1A]">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${colors.bgLight}`}>
            <StageIcon size={14} className={colors.text} />
          </div>
          <span className="text-xs text-[#A0A0A0]">{stageConfig.label} Stage</span>
        </div>
        <FlywheelMini currentStage={stage} />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 text-center">
        {/* Requirements */}
        <div className="p-2 bg-[#0A0A0A] rounded-lg">
          <div className="text-sm font-bold text-white">
            {coverage.covered}/{coverage.total || '-'}
          </div>
          <div className="text-[9px] text-[#5A5A5A] uppercase tracking-wider">Reqs</div>
        </div>
        
        {/* Days Left */}
        <div className="p-2 bg-[#0A0A0A] rounded-lg">
          <div className={`text-sm font-bold ${
            urgencyStatus === 'overdue' ? 'text-red-400' :
            urgencyStatus === 'urgent' ? 'text-amber-400' :
            urgencyStatus === 'warning' ? 'text-yellow-400' :
            'text-white'
          }`}>
            {daysLeft !== null ? (daysLeft < 0 ? 'Overdue' : daysLeft) : '-'}
          </div>
          <div className="text-[9px] text-[#5A5A5A] uppercase tracking-wider">
            {daysLeft !== null && daysLeft >= 0 ? 'Days' : ''}
          </div>
        </div>
        
        {/* Owner */}
        <div className="p-2 bg-[#0A0A0A] rounded-lg">
          <div className="flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-[#1A1A1A] flex items-center justify-center">
              <User size={10} className="text-[#6B6B6B]" />
            </div>
          </div>
          <div className="text-[9px] text-[#5A5A5A] uppercase tracking-wider truncate">
            {cycle.ownership?.cycle_owner?.user_name?.split(' ')[0] || 'TBD'}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function ActiveCyclesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCycles = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await apiFetch('/disclosure-cycles/list?limit=50');
        if (data?._error) throw new Error(data.message || 'Failed to fetch cycles');
        
        // Filter out duplicates by cycle_code or cycle_name, test cycles, and show only active cycles
        const uniqueCycles = [];
        const seenCodes = new Set();
        const seenNames = new Set();
        for (const cycle of (data.items || [])) {
          const code = cycle.cycle_code || cycle.id;
          const name = cycle.cycle_name || '';
          // Skip test cycles, duplicates, archived, and published
          const isTestCycle = name.includes('TEST_') || name.includes('Workflow Test') || code.includes('TEST_');
          const isDuplicate = seenCodes.has(code) || seenNames.has(name);
          if (!isDuplicate && !cycle.archived && cycle.status !== 'published' && !isTestCycle) {
            seenCodes.add(code);
            seenNames.add(name);
            uniqueCycles.push(cycle);
          }
        }
        setCycles(uniqueCycles);
      } catch (err) {
        console.error('Error fetching cycles:', err);
        setError(err.message);
        // Fallback mock data
        setCycles([
          {
            id: 'fy2024-csrd',
            cycle_name: 'FY2024 CSRD/ESRS Annual Report',
            cycle_code: 'FY2024-CSRD',
            framework_code: 'CSRD',
            current_stage: 'evidence_review',
            overall_progress: 65,
            filing_deadline: '2025-06-30',
            status: 'in_progress',
            ownership: { cycle_owner: { user_name: 'David Chen' } },
            requirements_coverage: { covered: 720, total: 1200 },
          },
          {
            id: '2025-cdp',
            cycle_name: '2025 CDP Climate Response',
            cycle_code: '2025-CDP',
            framework_code: 'CDP',
            current_stage: 'mapping',
            overall_progress: 35,
            filing_deadline: '2025-07-31',
            status: 'draft',
            ownership: { cycle_owner: { user_name: 'Jane Smith' } },
            requirements_coverage: { covered: 45, total: 120 },
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCycles();
  }, []);

  const filteredCycles = cycles.filter(cycle => {
    const name = cycle.cycle_name || '';
    const framework = cycle.framework_code || '';
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         framework.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || cycle.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Stats calculations
  const totalActive = cycles.length;
  const atRiskCount = cycles.filter(c => {
    const deadline = c.filing_deadline ? new Date(c.filing_deadline) : null;
    const daysLeft = deadline ? Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)) : null;
    return daysLeft !== null && daysLeft <= 30 && (c.overall_progress || 0) < 80;
  }).length;
  const openReqs = cycles.reduce((sum, c) => {
    const total = c.requirements_coverage?.total || 0;
    const covered = c.requirements_coverage?.covered || 0;
    return sum + Math.max(0, total - covered);
  }, 0);
  const nearestDeadline = cycles.reduce((min, c) => {
    if (!c.filing_deadline) return min;
    const days = Math.ceil((new Date(c.filing_deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 && days < min ? days : min;
  }, Infinity);

  return (
    <div className="space-y-6" data-testid="active-cycles-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Active Disclosure Cycles</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Track and manage your ongoing compliance reporting cycles
          </p>
        </div>
        <Link 
          to="/cycles/new"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="new-cycle-btn"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">New Cycle</span>
        </Link>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <FileText size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalActive}</div>
              <div className="text-xs text-[#6B6B6B]">Active Cycles</div>
            </div>
          </div>
        </div>
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <AlertTriangle size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{atRiskCount}</div>
              <div className="text-xs text-[#6B6B6B]">At Risk</div>
            </div>
          </div>
        </div>
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Zap size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{openReqs}</div>
              <div className="text-xs text-[#6B6B6B]">Open Requirements</div>
            </div>
          </div>
        </div>
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Clock size={18} className="text-cyan-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {nearestDeadline === Infinity ? '-' : nearestDeadline}
              </div>
              <div className="text-xs text-[#6B6B6B]">Next Deadline (days)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search cycles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="cycles-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
            data-testid="cycles-filter-status"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
          <span className="ml-3 text-[#A0A0A0]">Loading cycles...</span>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertTriangle size={16} />
            <span>Using cached data. API issue: {error}</span>
          </div>
        </div>
      )}

      {/* Cycles Grid */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredCycles.map(cycle => (
            <CycleCard key={cycle.id} cycle={cycle} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCycles.length === 0 && (
        <div className="text-center py-12 bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl">
          <FileText size={48} className="mx-auto text-[#2A2A2A] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No cycles found</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            {searchQuery || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter'
              : 'Create your first disclosure cycle'}
          </p>
          {!searchQuery && filterStatus === 'all' && (
            <Link 
              to="/cycles/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
            >
              <Plus size={16} />
              Create Cycle
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
